import api, { parseErrorMessage } from "./api";
import { API_ENDPOINTS } from "../config/api.config";
import { setAuthTokens, clearAuthTokens } from "./tokenService";

/**
 * 認證服務 - 業界最佳實踐
 * 統一處理所有認證相關操作
 */

/**
 * 用戶登錄
 * @param {string} username - 用戶名
 * @param {string} password - 密碼
 * @returns {Promise<Object>} 登錄結果
 */
export const login = async (username, password) => {
  try {
    console.log("🔐 Attempting login for user:", username);
    
    const response = await api.post(API_ENDPOINTS.AUTH.LOGIN, {
      username,
      password,
    });

    const { access, refresh } = response.data;
    
    if (!access || !refresh) {
      throw new Error("登入回傳資料不完整！");
    }

    // 使用統一的 token 設置函數
    setAuthTokens(access, refresh);
    
    console.log("✅ Login successful");
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    const errorMessage = parseErrorMessage(error);
    console.error("🛑 登入失敗:", errorMessage);
    
    return {
      success: false,
      error: errorMessage,
      status: error.response?.status,
      data: error.response?.data,
    };
  }
};

/**
 * 用戶登出
 */
export const logout = async () => {
  try {
    // 嘗試調用後端登出 API（可選）
    await api.post(API_ENDPOINTS.AUTH.LOGOUT);
  } catch (error) {
    // 即使後端登出失敗，也要清除本地 token
    console.warn("🚪 Backend logout failed, clearing local tokens:", error.message);
  } finally {
    clearAuthTokens();
    console.log("🚪 User logged out");
  }
};

/**
 * 獲取當前用戶信息（從 localStorage）
 * @returns {Object|null} 用戶信息
 */
export const getCurrentUser = () => {
  try {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  } catch (error) {
    console.error("👤 Failed to parse user data:", error);
    return null;
  }
};

/**
 * 檢查用戶是否已認證
 * @returns {boolean} 是否已認證
 */
export const isAuthenticated = () => {
  const accessToken = localStorage.getItem("access_token");
  return !!accessToken;
};

/**
 * 獲取用戶完整數據（從 API）
 * @returns {Promise<Object>} 用戶數據結果
 */
export const getUserData = async () => {
  try {
    console.log("👤 Fetching user data...");
    
    // 獲取基本用戶信息
    const userResponse = await api.get(API_ENDPOINTS.AUTH.USER);
    let userData = { ...userResponse.data };
    
    console.log("✅ Basic user data fetched:", userData);
    
    // 嘗試獲取權限信息，失敗時設置默認值
    try {
      console.log("🔒 Fetching user permissions...");
      const permissionsResponse = await api.get(API_ENDPOINTS.AUTH.PERMISSIONS);
      userData.permissions = permissionsResponse.data;
      console.log("✅ User permissions fetched successfully:", permissionsResponse.data);
    } catch (permissionsError) {
      console.error("❌ Permissions API error:", {
        status: permissionsError.response?.status,
        statusText: permissionsError.response?.statusText,
        url: permissionsError.config?.url,
        message: permissionsError.message
      });
      
      // 根據錯誤類型設置不同的默認權限
      if (permissionsError.response?.status === 404) {
        console.warn("⚠️ Permissions endpoint not found, using basic user permissions");
      } else if (permissionsError.response?.status === 403) {
        console.warn("⚠️ Access denied to permissions, using restricted permissions");
      } else {
        console.warn("⚠️ Unknown permissions error, using default permissions");
      }
      
      userData.permissions = {
        role: userData.role || 'user',
        role_display: userData.role === 'admin' ? '管理員' : 
                     userData.role === 'moderator' ? '版主' : '一般使用者',
        can_moderate: userData.role === 'moderator' || userData.role === 'admin',
        can_administrate: userData.role === 'admin',
        specific_permissions: {}
      };
    }
    
    // 保存到 localStorage
    localStorage.setItem("user", JSON.stringify(userData));
    
    console.log("✅ Complete user data processed successfully");
    return {
      success: true,
      data: userData,
    };
  } catch (error) {
    const errorMessage = parseErrorMessage(error);
    console.error("👤 獲取用戶數據失敗:", errorMessage);
    
    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * 獲取用戶的文章列表
 * @returns {Promise<Object>} 文章列表結果
 */
export const getUserPosts = async () => {
  try {
    console.log("📝 Fetching user posts...");
    const response = await api.get("/users/me/posts/");
    
    console.log("✅ User posts fetched successfully");
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    const errorMessage = parseErrorMessage(error);
    console.error("📝 獲取用戶文章失敗:", errorMessage);
    
    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * 刷新用戶權限
 * @returns {Promise<Object>} 權限數據結果
 */
export const refreshUserPermissions = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.AUTH.PERMISSIONS);
    
    // 更新 localStorage 中的用戶數據
    const currentUser = getCurrentUser();
    if (currentUser) {
      const updatedUser = {
        ...currentUser,
        permissions: response.data,
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));
    }
    
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    const errorMessage = parseErrorMessage(error);
    console.error("🔄 刷新權限失敗:", errorMessage);
    
    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * 用戶註冊
 * @param {Object} userData - 註冊數據
 * @returns {Promise<Object>} 註冊結果
 */
export const register = async (userData) => {
  try {
    console.log("📝 Attempting registration for user:", userData.username);
    
    const response = await api.post("/users/register/", userData);
    
    console.log("✅ Registration successful");
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    const errorMessage = parseErrorMessage(error);
    console.error("🛑 註冊失敗:", errorMessage);
    
    return {
      success: false,
      error: errorMessage,
      status: error.response?.status,
      data: error.response?.data,
    };
  }
};

// 向後兼容的導出
export const authService = {
  login,
  logout,
  getCurrentUser,
  isAuthenticated,
  getUserData,
  getUserPosts,
  refreshUserPermissions,
  register,
};
